/**
 * ContractDiscountButton — the "Contract discount" button that opens
 * ContractDiscountDialog. One button for every page that edits a discount
 * (Manage Portfolio, Cost Advice, Telemetry, Cloud Usage Reports), so its
 * label and its screen-reader text behave the same everywhere.
 *
 * Controlled: the page owns the value ({ onDemand, oneYearRi, threeYearRi });
 * Apply hands the new value to `onApply`.
 */
import { useState } from 'react';

import { PercentIcon } from '@components/common/icons';
import { Button } from '@components/ui';

import { contractDiscountLabel, isContractDiscountEmpty } from './contractDiscountRules';

import { ContractDiscountDialog } from './index';

/**
 * @param {object}   props
 * @param {object}   [props.value]       { onDemand, oneYearRi, threeYearRi } shown
 * @param {(value: object) => void} props.onApply
 * @param {boolean}  [props.disabled]
 * @param {string}   [props.title]       why it is off, on hover
 * @param {string}   [props.className]
 * @param {string}   [props.idPrefix]    unique field ids per page
 */
export function ContractDiscountButton({
  value,
  onApply,
  disabled = false,
  title,
  className,
  idPrefix,
}) {
  const [open, setOpen] = useState(false);
  const label = contractDiscountLabel(value);
  const empty = isContractDiscountEmpty(value);

  return (
    <>
      <Button
        variant="secondary-solid"
        className={className}
        startIcon={<PercentIcon />}
        disabled={disabled}
        title={title}
        aria-label={empty ? 'Set contract discount' : `${label}. Change`}
        onClick={() => setOpen(true)}
      >
        {label}
      </Button>
      {/* Mounted only while open, so each opening starts from `value`. */}
      {open ? (
        <ContractDiscountDialog
          idPrefix={idPrefix}
          value={value}
          onClose={() => setOpen(false)}
          onApply={(next) => {
            setOpen(false);
            onApply?.(next);
          }}
        />
      ) : null}
    </>
  );
}

export default ContractDiscountButton;
